import React from 'react';
import {
	Box,
	Button,
	Divider,
	Drawer,
	IconButton,
	List,
	ListItemButton,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ActiveOverstay } from 'api/canpark/overstays/getActiveOverstays';
import { ParkingLocation } from 'containers/pages/CANpark/types/parkingLocation';
import { useActiveVehicle } from 'containers/pages/CANpark/context/ActiveVehicleProvider';
import { useNotificationProvider } from 'hooks';

interface Props {
	open: boolean;
	onClose: () => void;
	overstays: ActiveOverstay[];
	dismiss: (ticketNumber: string) => void;
	dismissAll: () => void;
	getParkingLocation: (id: string) => ParkingLocation | undefined;
}

const formatElapsed = (loggedAt: string): string => {
	const ms = Date.now() - new Date(loggedAt).getTime();
	const totalMin = Math.max(0, Math.round(ms / 60000));
	const h = Math.floor(totalMin / 60);
	const m = totalMin % 60;
	if (h && m) return `${h}h ${m}m ago`;
	if (h) return `${h}h ago`;
	return `${m}m ago`;
};

const OverstayNotificationDrawer: React.FC<Props> = ({
	open,
	onClose,
	overstays,
	dismiss,
	dismissAll,
	getParkingLocation,
}) => {
	const { setCurrentSearch } = useActiveVehicle();
	const { notifyError } = useNotificationProvider();

	const handleRowClick = (o: ActiveOverstay) => {
		const parkingLocation = getParkingLocation(o.parkingLocationId);
		if (!parkingLocation) {
			notifyError('Parking lot details are still loading. Try again shortly.');
			return;
		}
		setCurrentSearch({
			parkingLocation,
			licenseNumber: o.licenseNumber,
			licenseState: o.licenseState,
		});
		onClose();
	};

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={onClose}
			PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					padding: 1.5,
				}}
			>
				<Typography variant="h6" fontWeight={300}>
					CANpark Notifications
				</Typography>
				<IconButton onClick={onClose} size="small">
					<CloseIcon />
				</IconButton>
			</Box>
			<Divider />
			<Box sx={{ padding: 1.5 }}>
				<Button
					variant="outlined"
					size="small"
					onClick={dismissAll}
					disabled={overstays.length === 0}
				>
					Dismiss all
				</Button>
			</Box>
			<Divider />

			{overstays.length === 0 ? (
				<Box sx={{ padding: 3, textAlign: 'center' }}>
					<Typography variant="body2" color="text.secondary">
						You have no notifications right now.
					</Typography>
					<Typography
						variant="caption"
						color="text.secondary"
						component="div"
						sx={{ mt: 1 }}
					>
						Overstay alerts from time-based lots will appear here.
					</Typography>
				</Box>
			) : (
				<List disablePadding>
					{overstays.map((o) => (
						<React.Fragment key={o.ticketNumber}>
							<ListItemButton
								onClick={() => handleRowClick(o)}
								sx={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'stretch',
									gap: 0.5,
									py: 1.5,
								}}
							>
								<Box
									display="flex"
									justifyContent="space-between"
									alignItems="center"
									gap={1}
								>
									<Typography variant="body1" fontWeight={500}>
										{o.licenseNumber}
										<Typography
											component="span"
											variant="caption"
											color="text.secondary"
											sx={{ ml: 1 }}
										>
											{o.licenseState}
										</Typography>
									</Typography>
									<Box display="flex" gap={0.5}>
										<Button
											variant="outlined"
											size="small"
											onClick={(e) => {
												e.stopPropagation();
												handleRowClick(o);
											}}
										>
											Open
										</Button>
										<Button
											variant="text"
											size="small"
											onClick={(e) => {
												e.stopPropagation();
												dismiss(o.ticketNumber);
											}}
										>
											Dismiss
										</Button>
									</Box>
								</Box>
								<Typography variant="caption" color="text.secondary">
									{o.parkingLocationName}
								</Typography>
								{o.vehicleInfo && (
									<Typography variant="caption" color="text.secondary">
										{o.vehicleInfo}
									</Typography>
								)}
								<Typography variant="caption" color="error.main">
									Logged {formatElapsed(o.loggedAt)} — exceeded by{' '}
									{Math.max(0, o.minutesOverstayed - o.maxStayMinutes)} min
								</Typography>
							</ListItemButton>
							<Divider />
						</React.Fragment>
					))}
				</List>
			)}
		</Drawer>
	);
};

export default OverstayNotificationDrawer;
